import Button from 'components/Form/Button';
import BasicTable from 'components/Form/Table';
import UploadFile from 'components/Form/UploadFile';
import ModalAcoesDocumento from 'components/Modal/ExpedicaoModal';
import TableWrapper from 'components/TableWrapper';
import ContainerAuthenticated from 'containers/Authenticated';
import { CoreContext } from 'context/CoreContext';
import moment from 'moment';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { Read, Update } from 'services/financing-desk-controls';
import {
  Load,
  StatusContent,
  TableInfo,
  TableInfoPend,
  Touch,
} from 'ui/styled';
import {
  exposeStrapiError,
  formatarValor,
  marcadorDiferente,
  parseStrapiImage,
  selectColors,
} from 'utils';
import { formatadores, normalizeName } from 'utils/validators';

export default function ControlDeskExpedicao() {
  const { user, filterExpression, setModal } = useContext(CoreContext);
  const [loading, setLoading] = useState(false);
  const [registers, setRegisters] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedProposal, setSelectedProposal] = useState(null);
  const [isClosing, setIsClosing] = useState(false);
  const [nresult, setNresult] = useState(false);
  const handleOpenModal = useCallback(
    (row) => {
      let expedi = nresult.filter((index) => index.id === row.id);
      if (!expedi[0].documento_expedicao) {
        toast.error(
          'É necessário ter ao menos um documento anexado (NF de Expedição) para aprovar.'
        );
        return;
      }
      setSelectedProposal(row.proposta);
      setIsClosing(false);
      setIsModalOpen(true);
    },
    [nresult]
  );
  const handleCloseModal = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsModalOpen(false);
      setSelectedProposal(null);
    }, 400);
  };
  const save = useCallback(
    async (payload, item, mode) => {
      if (loading) return;
      setLoading(`${item?.id}-${mode}`);
      const result = await Update(
        {
          data: {
            documento_expedicao: payload.documento_expedicao,
            tag: payload.tag,
            data_expedicao: new Date().toISOString().split('T')[0],
          },
        },
        item?.id
      );
      setLoading(false);
      if (result && !exposeStrapiError(result)) {
        toast.success('Nota Fiscal de Expedição anexada com sucesso!');
        // Atualiza o estado local imediatamente
        setRegisters((prev) =>
          prev.map((reg) =>
            reg.id === item.id
              ? {
                  ...reg,
                  documento_expedicao: result?.data?.documento_expedicao || {
                    url: `temp_${payload.documento_expedicao}`,
                  }, // Valor temporário
                  tag: result?.data?.tag || payload.tag,
                }
              : reg
          )
        );
        // Aguarda 2 segundos antes de chamar init para dar tempo ao backend
        setTimeout(() => init(), 2000);
      } else {
        toast.error(
          'Erro ao anexar a Nota Fiscal de Expedição. Tente novamente.'
        );
        console.error('Erro ao anexar expedição:', result);
      }
    },
    [loading]
  );
  const columns = useMemo(() => {
    const defaultColumns = [
      {
        title: 'Id',
        sortable: true,
        ref: 'id',
        renderCell: ({ row }) => {
          // Condição: Se o campo 'curadoria_analise' for 'Analisado'
          const isAnalisado = row?.curadoria_analise === 'Analisado';
          const id = row?.proposta_financeira?.id || row?.proposta;
          if (isAnalisado) {
            return marcadorDiferente(id, '!');
          }
          return id;
        },
      },
      {
        title: 'Data Entrada Expedição',
        sortable: true,
        ref: 'data_entrada_expedicao',
        align: 'center',
        renderCell: ({ row }) =>
          formatadores.TIMESTAMP_TO_BR(row?.data_entrada_expedicao),
      },

      { title: 'Proposta', ref: 'cod_proposta_externa' },
      ...(user?.administrador ||
      (user?.distribuidores && user.distribuidores.length >= 1)
        ? [{ title: 'Distribuidor', sortable: true, ref: 'distributor' }]
        : []),
      { title: 'Integrador', sortable: true, ref: 'integrator' },
      { title: 'Cliente', sortable: true, ref: 'cliente' },
      { title: 'Cidade', ref: 'cidade' },
      { title: 'UF', ref: 'uf' },
      { title: 'Concessionária', ref: 'concessionaria' },
      { title: 'KWP', ref: 'kwp' },
      {
        title: 'NF Expedição',
        ref: 'documento_expedicao',
        renderCell: ({ row }) => {
          const anexo = row?.original?.documento_expedicao;
          if (loading === `${row?.id}-documento_expedicao`) {
            return <Load />;
          }
          if (anexo?.url) {
            return (
              <Touch
                onClick={() =>
                  window.open(parseStrapiImage(anexo.url), '_blank')
                }
              >
                <TableInfo>Anexado</TableInfo>
              </Touch>
            );
          }
          return (
            <UploadFile
              key={row.id}
              onChange={(file) =>
                save(
                  { documento_expedicao: file?.id, tag: 'Docs. em análise' },
                  row.original,
                  'documento_expedicao'
                )
              }
              proposalType="Expedição"
            >
              <TableInfoPend>Anexar</TableInfoPend>
            </UploadFile>
          );
        },
      },
      {
        title: 'TAG',
        ref: 'tag',
        renderCell: ({ row }) => (
          <StatusContent
            color={selectColors(
              row?.tag === 'Aguardando anexar docs' ? 'Em Análise' : ''
            )}
          >
            {row?.tag === 'Aguardando anexar docs'
              ? 'Aguardando anexar docs'
              : 'Em Análise'}
          </StatusContent>
        ),
      },
      ...(user?.administrador
        ? [
            {
              title: 'Ações',
              renderCell: ({ row }) => (
                <Button
                  onClick={() => handleOpenModal(row)}
                  color="blue"
                  nospace
                  mbutton
                  small
                >
                  Ações
                </Button>
              ),
            },
            {
              title: 'Observações',
              renderCell: ({ row }) => (
                <Button
                  onClick={() => {
                    setModal({
                      type: 'observacao',
                      dados: row,
                      origem: 'Expedição',
                      reload: () => init(),
                    });
                  }}
                  color="botao"
                  nospace
                  mbutton
                  small
                >
                  Adicionar
                </Button>
              ),
            },
          ]
        : []),
    ];
    return defaultColumns.filter((f) => f);
  }, [loading, user, handleOpenModal, save, setModal]);
  const rows = useMemo(() => {
    return registers?.map((item) => {
      const propostaFinanceira = item?.proposta_financeira;
      const cliente = propostaFinanceira?.informacoes_cliente;
      const endereco = propostaFinanceira?.endereco_instalacao;
      const projeto = propostaFinanceira?.informacoes_projeto;
      return {
        proposta: propostaFinanceira?.id,
        id: item?.id,
        original: item,
        cod_proposta_externa: normalizeName(
          propostaFinanceira?.cod_proposta_externa
        ),
        data_entrada_expedicao: item?.data_entrada_expedicao || item?.createdAt,
        formatedDate: formatadores.TIMESTAMP_TO_BR(
          item?.data_entrada_expedicao || item?.createdAt
        ),
        distributor: normalizeName(
          propostaFinanceira?.distribuidor?.trade_name
        ),
        integrator: normalizeName(
          propostaFinanceira?.integrador?.nome_fantasia
        ),
        cliente: normalizeName(cliente?.nome_completo),
        cidade: normalizeName(endereco?.cidade),
        uf: endereco?.uf,
        concessionaria: normalizeName(projeto?.concessionaria),
        kwp: formatarValor(projeto?.potencia_kWp),
        documento_expedicao: item?.documento_expedicao?.url
          ? 'Anexado'
          : 'Anexar',
        tag:
          item?.tag ||
          (item?.documento_expedicao?.url
            ? 'Docs. em análise'
            : 'Aguardando anexar docs'),
        curadoria_analise: item?.proposta_financeira?.curadoria_analise,
        curadoria_analise_bot: item?.proposta_financeira?.curadoria_analise_bot,
        // cnpj_integrador: propostaFinanceira.integrador?.cnpj,
        // cnpj_integrador_clean: propostaFinanceira.integrador?.cnpj.replace(
        //   /\D/g,
        //   ''
        // ),
        // cpf_cnpj: cliente?.cpf || item?.cliente?.cnpj,
        // cpf_cnpj_clean: cliente?.cpf
        //   ? item?.cliente?.cpf.replace(/\D/g, '')
        //   : item?.cliente?.cnpj.replace(/\D/g, ''),
        cpf_cnpj: item?.cpf_cliente || '-',
        cpf_cnpj_clean:
          item?.cpf_cliente ?? item?.cpf_cliente.replace(/\D/g, ''),
      };
    });
  }, [registers]);

  const init = async () => {
    setLoading(true);
    const populateParams =
      '&populate[proposta_financeira][populate][distribuidor][fields][0]=trade_name' +
      '&populate[proposta_financeira][populate][integrador][fields][0]=nome_fantasia' +
      '&populate[proposta_financeira][populate][informacoes_cliente]=*' +
      '&populate[proposta_financeira][populate][endereco_instalacao]=*' +
      '&populate[proposta_financeira][populate][informacoes_projeto]=*' +
      '&populate[documento_expedicao]=*';
    const timestamp = new Date().getTime();
    const result = await Read(
      `?filters[$or][0][status][$eq]=Expedição` +
        '&filters[proposta_financeira][id][$notIn][0]=null' +
        populateParams +
        `&_=${timestamp}`
    );
    setLoading(false);
    if (result && !exposeStrapiError(result)) {
      const nresult = result.results
        ?.map((m) => {
          // const proposta_financeira = normalizeStrapiRegister(m?.proposta_financeira)
          // const parecer_acesso_midia = normalizeStrapiRegister(m?.parecer_acesso_midia)
          // const bank = proposta_financeira?.propostas_banco?.find(f => f.status == "aprovado")
          return {
            ...m,
            // proposta_financeira,
            // parecer_acesso_midia,
            // bank: {
            //  ...bank,
            //  banco: normalizeStrapiRegister(bank?.banco)
            // }
          };
        })
        ?.sort((a, b) => {
          // Pega a data de contato para a proposta 'a'
          const dateA = moment(a.data_entrada_conexao || a.data_enviar_conexao);
          // Pega a data de contato para a proposta 'b'
          const dateB = moment(b.data_entrada_conexao || b.data_enviar_conexao);
          // Retorna a diferença
          // Se dateA for mais antiga, o resultado é negativo e 'a' é colocado antes de 'b'.
          return dateA - dateB;
        });
      setNresult(nresult);
      setRegisters(nresult);
    } else {
      console.error('Erro ao carregar dados (init):', result);
    }
  };

  useEffect(() => init(), []);

  return (
    <>
      <ContainerAuthenticated>
        <TableWrapper title={'Expedição'} search>
          <BasicTable
            loading={loading}
            columns={columns}
            rows={rows?.filter(filterExpression)}
            pagination
          />
        </TableWrapper>
      </ContainerAuthenticated>
      {isModalOpen && selectedProposal && (
        <ModalAcoesDocumento
          codPropostaExterna={selectedProposal.cod_proposta_externa}
          id={selectedProposal.id}
          // proposta_financeira={selectedProposal.proposta_financeira}
          onClose={handleCloseModal}
          reloadContent={init}
          isClosing={isClosing}
          documento_expedicao={selectedProposal.url}
          proposta={selectedProposal}
        />
      )}
    </>
  );
}
